<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { createLatestRequest } from 'dashboard/helper/latestRequest';
import DealReportsAPI from 'dashboard/api/dealReports';
import Button from 'dashboard/components-next/button/Button.vue';
import OperationalReport from './dealReports/components/OperationalReport.vue';

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const tracker = createLatestRequest();
const isLoading = ref(false);
const report = ref(null);
const selectedPeriod = ref('today');
const customStartDate = ref('');
const customEndDate = ref('');
const selectedAgentIds = ref([]);

const agentOptions = computed(() =>
  (agents.value || []).map(agent => ({ id: agent.id, name: agent.name }))
);

const selectedAgents = computed({
  get: () =>
    agentOptions.value.filter(agent =>
      selectedAgentIds.value.includes(agent.id)
    ),
  set: value => {
    selectedAgentIds.value = value ? value.map(agent => agent.id) : [];
  },
});

const periodOptions = computed(() => [
  { value: 'today', label: t('DEAL_REPORTS.OPERATIONS.TODAY') },
  { value: '7d', label: t('DEAL_REPORTS.FILTERS.PERIOD_7D') },
  { value: '30d', label: t('DEAL_REPORTS.FILTERS.PERIOD_30D') },
  { value: '90d', label: t('DEAL_REPORTS.FILTERS.PERIOD_90D') },
  { value: 'custom', label: t('DEAL_REPORTS.FILTERS.PERIOD_CUSTOM') },
]);

const formatLocalDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateRange = computed(() => {
  const end = new Date();
  const start = new Date();
  const days = { today: 0, '7d': 7, '30d': 30, '90d': 90 };

  if (selectedPeriod.value === 'custom') {
    return {
      start_date: customStartDate.value,
      end_date: customEndDate.value,
    };
  }

  start.setDate(start.getDate() - days[selectedPeriod.value]);
  return {
    start_date: formatLocalDate(start),
    end_date: formatLocalDate(end),
  };
});

const isCustomIncomplete = computed(
  () =>
    selectedPeriod.value === 'custom' &&
    !(customStartDate.value && customEndDate.value)
);

const loadReport = async () => {
  const id = tracker.next();

  // Período personalizado incompleto: avisa e zera, não mantém números antigos.
  if (isCustomIncomplete.value) {
    report.value = null;
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  const [result] = await Promise.allSettled([
    DealReportsAPI.getOperations({
      ...dateRange.value,
      agent_ids: selectedAgentIds.value,
    }),
  ]);
  if (!tracker.isLatest(id)) return;

  if (result.status === 'fulfilled') {
    report.value = result.value.data;
  } else {
    report.value = null;
    useAlert(t('DEAL_REPORTS.PARTIAL_ERROR', { count: 1 }));
  }
  isLoading.value = false;
};

watch(
  [selectedPeriod, customStartDate, customEndDate, selectedAgentIds],
  loadReport
);
onMounted(async () => {
  if (!agents.value?.length) await store.dispatch('agents/get');
  await loadReport();
});
</script>

<template>
  <div class="min-h-full bg-n-background p-6">
    <div
      class="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
    >
      <div class="min-w-48 flex-1">
        <label class="mb-1 block text-sm font-medium text-n-slate-11">
          {{ t('DEAL_REPORTS.FILTERS.PERIOD') }}
        </label>
        <select
          v-model="selectedPeriod"
          class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
        >
          <option
            v-for="option in periodOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
      <template v-if="selectedPeriod === 'custom'">
        <div class="min-w-40 flex-1">
          <label class="mb-1 block text-sm font-medium text-n-slate-11">
            {{ t('DEAL_REPORTS.FILTERS.START_DATE') }}
          </label>
          <input
            v-model="customStartDate"
            type="date"
            class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
          />
        </div>
        <div class="min-w-40 flex-1">
          <label class="mb-1 block text-sm font-medium text-n-slate-11">
            {{ t('DEAL_REPORTS.FILTERS.END_DATE') }}
          </label>
          <input
            v-model="customEndDate"
            type="date"
            class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
          />
        </div>
      </template>
      <div class="min-w-64 flex-[2] multiselect-wrap--small">
        <label class="mb-1 block text-sm font-medium text-n-slate-11">
          {{ t('DEAL_REPORTS.FILTERS.USERS') }}
        </label>
        <multiselect
          v-model="selectedAgents"
          :options="agentOptions"
          :placeholder="t('DEAL_REPORTS.FILTERS.ALL_USERS')"
          label="name"
          track-by="id"
          multiple
          :max-height="200"
          selected-label=""
          :select-label="t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
        />
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        :is-loading="isLoading"
        slate
        faded
        @click="loadReport"
      >
        {{ t('DEAL_REPORTS.REFRESH') }}
      </Button>
    </div>

    <div v-if="isLoading" class="mb-4 h-0.5 w-full animate-pulse bg-n-brand" />

    <p v-if="isCustomIncomplete" class="mb-4 text-sm text-n-amber-11">
      {{ t('DEAL_REPORTS.FILTERS.CUSTOM_INCOMPLETE') }}
    </p>

    <div
      v-if="isLoading && !report"
      class="flex justify-center py-20 text-n-slate-10"
    >
      {{ t('DEAL_REPORTS.LOADING') }}
    </div>
    <OperationalReport v-else-if="report" :data="report" />
  </div>
</template>
